'use client';

/**
 * Register page — ui-ux-pro-max gold/cream shell
 * Single brand mark in navbar; visible labels; strength meter; a11y.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Form, Input, Button, Select, Tooltip } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  KeyOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  UpOutlined,
  SafetyOutlined,
  CloseOutlined,
  LoginOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/domains/auth/context/auth-context';
import {
  getPasswordRequirements,
  getPasswordStrength,
  PASSWORD_STRENGTH_COLORS,
  type PasswordRequirement,
} from '@/domains/auth/validation/password-policy';
import { useTheme, ThemeToggle } from '@/shared/theme';
import '@/shared/theme/styles/login.css';
import styles from './AuthLayout.module.css';

const RegisterPage: React.FC = () => {
  const [form] = Form.useForm();
  const router = useRouter();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [activeField, setActiveField] = useState<'password' | 'confirmPassword' | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const theme = useTheme();
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const formPassword = Form.useWatch('password', form);
  const formConfirmPassword = Form.useWatch('confirmPassword', form);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.getModifierState) {
        setCapsLockOn(e.getModifierState('CapsLock'));
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.getModifierState) {
        setCapsLockOn(e.getModifierState('CapsLock'));
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  useEffect(() => {
    const currentPassword = formPassword || password;
    const currentConfirmPassword = formConfirmPassword || confirmPassword;
    setPasswordsMatch(
      currentPassword === currentConfirmPassword &&
        currentPassword.length > 0 &&
        currentConfirmPassword.length > 0
    );
  }, [formPassword, formConfirmPassword, password, confirmPassword]);

  useEffect(() => {
    if (!error) return;
    const t = window.setTimeout(() => errorRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [error]);

  const generatePassword = () => {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '@$!%*?&';
    const allChars = lowercase + uppercase + numbers + symbols;
    let p = '';
    p += lowercase[Math.floor(Math.random() * lowercase.length)];
    p += uppercase[Math.floor(Math.random() * uppercase.length)];
    p += numbers[Math.floor(Math.random() * numbers.length)];
    p += symbols[Math.floor(Math.random() * symbols.length)];
    for (let i = 4; i < 16; i++) p += allChars[Math.floor(Math.random() * allChars.length)];
    return p
      .split('')
      .sort(() => Math.random() - 0.5)
      .join('');
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    form.setFieldsValue({ password: newPassword, confirmPassword: newPassword });
    setPassword(newPassword);
    setConfirmPassword(newPassword);
    setTimeout(() => {
      form.validateFields(['password', 'confirmPassword']).catch(() => {});
    }, 0);
  };

  const onFinish = async (values: {
    username: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    roles?: string[];
  }) => {
    try {
      setLoading(true);
      setError(null);
      const userData = {
        username: values.username,
        email: values.email,
        password: values.password,
        first_name: values.firstName,
        last_name: values.lastName,
        roles: values.roles || ['trader'],
      };
      const response = await register(userData);
      if (response.success) router.push('/auth/login');
      else setError(response.error || 'Registration failed');
    } catch (err) {
      console.error('Registration error:', err);
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginClick = () => router.push('/auth/login');
  const currentPwd = formPassword || password;
  const emailValue = form.getFieldValue('email') || '';
  const requirements =
    currentPwd.length > 0 ? getPasswordRequirements(currentPwd, emailValue) : [];
  const strength = currentPwd.length > 0 ? getPasswordStrength(currentPwd, emailValue) : null;
  const strengthColor = strength ? PASSWORD_STRENGTH_COLORS[strength] : undefined;

  return (
    <div
      className={`login-page ${theme.isDark ? 'dark' : 'light'} ${styles.page}`}
      data-theme={theme.isDark ? 'dark' : 'light'}
    >
      <nav className="login-navbar" aria-label="Primary">
        <div className="login-navbar-container">
          <a href="/" className="login-navbar-brand">
            <div className="login-logo-icon" aria-hidden>
              <span className="login-logo-text">M</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span className="login-brand-title">Mechagora</span>
              <span className="login-brand-subtitle">Trading Platform</span>
            </div>
          </a>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              type="text"
              icon={<LoginOutlined aria-hidden />}
              onClick={handleLoginClick}
              className={styles.ghostBtn}
            >
              {isMobile ? 'Login' : 'Sign in'}
            </Button>
            <ThemeToggle size={isMobile ? 'small' : 'medium'} />
          </div>
        </div>
      </nav>

      <div className={`${styles.body} ${styles.bodyTop}`}>
        <div className={`${styles.formContainer} ${styles.formContainerWide}`}>
          <div className={styles.card}>
            <header className={styles.formHeader}>
              <h1 className={styles.formTitle}>Create account</h1>
              <p className={styles.formSubtitle}>Join our institutional trading platform</p>
            </header>

            {error && (
              <div
                ref={errorRef}
                className={styles.errorAlert}
                role="alert"
                tabIndex={-1}
                id="register-error"
              >
                <ExclamationCircleOutlined className={styles.errorIcon} aria-hidden />
                <div className={styles.errorContent}>
                  <div className={styles.errorTitle}>Registration failed</div>
                  <div className={styles.errorText}>{error}</div>
                </div>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => setError(null)}
                  className={styles.dismissBtn}
                  aria-label="Dismiss error"
                />
              </div>
            )}

            <Form
              form={form}
              name="register"
              onFinish={onFinish}
              layout="vertical"
              requiredMark={false}
              className={styles.formFields}
              disabled={loading}
              aria-busy={loading}
            >
              <div className={styles.formSection}>
                <div className={styles.sectionTitle}>Personal information</div>
                <div className={styles.formGrid}>
                  <Form.Item
                    name="firstName"
                    htmlFor="register-first-name"
                    label={<span className={styles.fieldLabel}>First name</span>}
                    rules={[
                      { required: true, message: 'First name is required' },
                      { min: 2, message: 'Minimum 2 characters' },
                    ]}
                  >
                    <Input
                      id="register-first-name"
                      name="firstName"
                      placeholder="First name"
                      autoComplete="given-name"
                      aria-required="true"
                    />
                  </Form.Item>
                  <Form.Item
                    name="lastName"
                    htmlFor="register-last-name"
                    label={<span className={styles.fieldLabel}>Last name</span>}
                    rules={[
                      { required: true, message: 'Last name is required' },
                      { min: 2, message: 'Minimum 2 characters' },
                    ]}
                  >
                    <Input
                      id="register-last-name"
                      name="lastName"
                      placeholder="Last name"
                      autoComplete="family-name"
                      aria-required="true"
                    />
                  </Form.Item>
                </div>
                <Form.Item
                  name="email"
                  htmlFor="register-email"
                  label={<span className={styles.fieldLabel}>Email address</span>}
                  rules={[
                    { required: true, message: 'Email is required' },
                    { type: 'email', message: 'Enter a valid email' },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined aria-hidden />}
                    type="email"
                    id="register-email"
                    name="email"
                    placeholder="email@company.com"
                    autoComplete="email"
                    autoCapitalize="none"
                    spellCheck={false}
                    inputMode="email"
                    aria-required="true"
                    aria-describedby={error ? 'register-error' : undefined}
                  />
                </Form.Item>
              </div>

              <div className={styles.formSection}>
                <div className={styles.sectionTitle}>Account credentials</div>
                <Form.Item
                  name="username"
                  htmlFor="register-username"
                  label={<span className={styles.fieldLabel}>Username</span>}
                  rules={[
                    { required: true, message: 'Username is required' },
                    { min: 3, message: 'Minimum 3 characters' },
                    {
                      pattern: /^[a-zA-Z0-9_]+$/,
                      message: 'Letters, numbers, and underscores only',
                    },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined aria-hidden />}
                    id="register-username"
                    name="username"
                    placeholder="username"
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    aria-required="true"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  htmlFor="register-password"
                  label={<span className={styles.fieldLabel}>Password</span>}
                  rules={[
                    { required: true, message: 'Password is required' },
                    { min: 12, message: 'Minimum 12 characters' },
                    {
                      // eslint-disable-next-line no-useless-escape
                      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&()_+\-=\[\]{};':"\\|,.<>\/?`~])[A-Za-z\d@$!%*?&()_+\-=\[\]{};':"\\|,.<>\/?`~]+/,
                      message: 'Must contain uppercase, lowercase, number, and special character',
                    },
                  ]}
                >
                  <div className={styles.passwordWrapper}>
                    <Input.Password
                      prefix={<LockOutlined aria-hidden />}
                      id="register-password"
                      name="password"
                      placeholder="Enter password"
                      autoComplete="new-password"
                      aria-required="true"
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setActiveField('password')}
                      onBlur={() => setActiveField(null)}
                    />
                    {capsLockOn && activeField === 'password' && (
                      <div className={styles.passwordIndicator} style={{ color: '#faad14' }} aria-label="Caps Lock is on">
                        <UpOutlined aria-hidden />
                      </div>
                    )}
                    {formPassword && passwordsMatch && (
                      <div className={styles.passwordIndicator} style={{ color: '#52c41a' }} aria-hidden>
                        <CheckCircleOutlined />
                      </div>
                    )}
                  </div>
                </Form.Item>

                <div style={{ marginBottom: 16 }}>
                  <Tooltip title="Generate secure 16-character password">
                    <Button
                      type="default"
                      icon={<KeyOutlined aria-hidden />}
                      onClick={handleGeneratePassword}
                      className={styles.generateBtn}
                    >
                      Generate secure password
                    </Button>
                  </Tooltip>
                </div>

                {strength && strengthColor && (
                  <div className={styles.strengthPanel} aria-live="polite">
                    <div className={styles.strengthHeader} style={{ color: strengthColor }}>
                      <SafetyOutlined aria-hidden />
                      <span style={{ textTransform: 'capitalize' }}>{strength} password</span>
                    </div>
                    <div className={styles.strengthBars} aria-hidden>
                      {(['weak', 'fair', 'good', 'strong'] as const).map((level, i) => (
                        <div
                          key={level}
                          className={styles.strengthBar}
                          style={{
                            background:
                              i <= (['weak', 'fair', 'good', 'strong'] as const).indexOf(strength)
                                ? strengthColor
                                : undefined,
                          }}
                        />
                      ))}
                    </div>
                    <div className={styles.requirementsGrid}>
                      {requirements.map((req: PasswordRequirement) => (
                        <div key={req.id} className={styles.requirementItem}>
                          {req.met ? (
                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} aria-hidden />
                          ) : (
                            <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 12 }} aria-hidden />
                          )}
                          <span style={{ color: req.met ? undefined : '#ff4d4f' }}>{req.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Form.Item
                  name="confirmPassword"
                  htmlFor="register-confirm-password"
                  label={<span className={styles.fieldLabel}>Confirm password</span>}
                  dependencies={['password']}
                  rules={[
                    { required: true, message: 'Confirm your password' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Passwords do not match'));
                      },
                    }),
                  ]}
                >
                  <div className={styles.passwordWrapper}>
                    <Input.Password
                      prefix={<LockOutlined aria-hidden />}
                      id="register-confirm-password"
                      name="confirmPassword"
                      placeholder="Confirm password"
                      autoComplete="new-password"
                      aria-required="true"
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onFocus={() => setActiveField('confirmPassword')}
                      onBlur={() => setActiveField(null)}
                    />
                    {capsLockOn && activeField === 'confirmPassword' && (
                      <div className={styles.passwordIndicator} style={{ color: '#faad14' }} aria-label="Caps Lock is on">
                        <UpOutlined aria-hidden />
                      </div>
                    )}
                    {(formConfirmPassword || confirmPassword).length > 0 && (
                      <div
                        className={styles.passwordIndicator}
                        style={{ color: passwordsMatch ? '#52c41a' : '#ff4d4f' }}
                        aria-hidden
                      >
                        {passwordsMatch ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                      </div>
                    )}
                  </div>
                </Form.Item>
              </div>

              <div className={styles.formSection}>
                <div className={styles.sectionTitle}>Professional information</div>
                <div className={styles.formGrid}>
                  <Form.Item
                    name="department"
                    htmlFor="register-department"
                    label={<span className={styles.fieldLabel}>Department</span>}
                  >
                    <Select
                      id="register-department"
                      placeholder="Select department"
                      allowClear
                      options={[
                        { value: 'trading', label: 'Trading' },
                        { value: 'risk', label: 'Risk Management' },
                        { value: 'compliance', label: 'Compliance' },
                        { value: 'technology', label: 'Technology' },
                        { value: 'operations', label: 'Operations' },
                        { value: 'research', label: 'Research' },
                        { value: 'sales', label: 'Sales' },
                        { value: 'other', label: 'Other' },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item
                    name="employeeId"
                    htmlFor="register-employee-id"
                    label={<span className={styles.fieldLabel}>Employee ID</span>}
                  >
                    <Input
                      id="register-employee-id"
                      name="employeeId"
                      placeholder="Employee ID"
                      autoComplete="off"
                    />
                  </Form.Item>
                </div>
                <Form.Item
                  name="managerId"
                  htmlFor="register-manager-id"
                  label={<span className={styles.fieldLabel}>Manager ID</span>}
                >
                  <Input
                    id="register-manager-id"
                    name="managerId"
                    placeholder="Manager's employee ID"
                    autoComplete="off"
                  />
                </Form.Item>
              </div>

              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                icon={!loading ? <ArrowRightOutlined /> : undefined}
                iconPlacement="end"
                className={styles.submitBtn}
              >
                {loading ? 'Creating account…' : 'Create account'}
              </Button>
            </Form>

            <div className={styles.divider} role="separator">
              <div className={styles.dividerLine} />
              <span className={styles.dividerText}>or</span>
              <div className={styles.dividerLine} />
            </div>

            <div className={styles.footerRow}>
              Already have an account?{' '}
              <button type="button" onClick={handleLoginClick} className={styles.linkBtn} style={{ background: 'none', border: 'none', font: 'inherit' }}>
                Sign in
              </button>
            </div>

            <div className={styles.securityBadge}>
              <SafetyOutlined className={styles.securityBadgeIcon} aria-hidden />
              Enterprise-grade security and encryption
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
